import { ArrowRight, CheckCircle, Users, Calendar, Rocket, Award, Zap } from "lucide-react";
import { Button } from "./ui/button";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "../translations";
import { useRefonte } from "../translations/refonte";
import { PilotTimeline } from "./PilotTimeline";

const PilotSection = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const n = useRefonte(language).nav;

  const paramIcons = [
    <Users className="w-5 h-5" />,
    <Calendar className="w-5 h-5" />,
    <Rocket className="w-5 h-5" />,
  ];

  const scrollToDemo = () => {
    const element = document.querySelector("#demo-form");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="pilot"
      className="pilot-section py-14 lg:py-16 relative overflow-hidden"
      data-testid="pilot-section"
    >
      {/* Background image - team collaboration */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://customer-assets.emergentagent.com/job_teams-integration/artifacts/hke77bc9_Gemini_Generated_Image_10bcrb10bcrb10bc.png"
          alt=""
          className="w-full h-full object-cover opacity-10"
          style={{ objectPosition: 'center bottom' }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#123A63] via-[#0B2D52] to-[#0A2342]"></div>
        <div className="absolute inset-0 dots-drift opacity-60" aria-hidden="true"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-400/20 border border-amber-300/40 rounded-full mb-6">
            <Zap className="w-4 h-4 text-amber-300" />
            <span className="text-sm font-semibold text-[#FDE68A]">
              {n.pilotProgram}
            </span>
          </div>
          <h2 className="font-bold text-3xl sm:text-4xl text-white tracking-tight mb-4">
            {t.pilot.headline}
          </h2>
          <p className="text-lg text-[#EAF3FD] leading-relaxed mb-8">
            {t.pilot.subheadline}
          </p>

        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left - What You'll Get */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
            <h3 className="font-semibold text-lg text-white mb-5">
              {t.pilot.whatYouGet}
            </h3>
            <div className="flex flex-col gap-3">
              {t.pilot.benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3"
                  data-testid={`pilot-benefit-${index}`}
                >
                  <CheckCircle className="w-5 h-5 text-[#34D399] flex-shrink-0 mt-0.5" />
                  <span className="text-white/90 text-sm">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Pilot Parameters & CTA */}
          <div className="flex flex-col justify-between">
            <div className="flex flex-col gap-3 mb-6">
              <h3 className="font-semibold text-lg text-white mb-4">
                {t.pilot.parameters}
              </h3>
              {t.pilot.params.map((param, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-xl"
                >
                  <div className="flex items-center gap-3 text-[#CFE6FC] text-sm">
                    {paramIcons[index]}
                    <span>{param.label}</span>
                  </div>
                  <span className="font-semibold text-white mono text-sm">
                    {param.value}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex flex-col items-center">
              <Button
                onClick={() => window.location.href = '/pilot'}
                className="w-full rounded-full px-8 py-4 bg-white text-[#1E3A8A] font-semibold hover:bg-[#F3F8FE] transition-all text-lg shadow-lg"
                data-testid="pilot-cta-button"
              >
                {t.pilot.cta}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        <PilotTimeline />
      </div>
    </section>
  );
};

export default PilotSection;
